import { useState, useEffect, useMemo } from 'react';
import { Select, Checkbox, Typography } from 'antd';
import { useRouter } from 'next/router';
import { openNotification } from 'util/helpers';
import { ASSETS_2022_CDN } from 'util/constants';
import {
  useUpdateUserSceneMutation,
  useUserScenesQuery,
  UserScenesDocument,
  SceneType,
  useGetMyTeamQuery,
} from '@src/components/generated/graphql';
import { LIGHT_TYPE } from '@/components/scene/react-three-components/lightTemplate';
import { debounce } from 'lodash';
import type { TemplateLight } from '@/components/menu/sideDrawer';
import { EnvMapType } from '@src/components/generated/graphql';

const { Text, Title } = Typography;

const sceneMapping = {
  [SceneType.Default]: 'Default scene',
  [SceneType.Qatar]: 'Qatar',
  [SceneType.FightGame_2D]: 'Fight Game 2D',
  [SceneType.RpgGame]: 'RPG Game',
  [SceneType.KidsGame]: 'Qatar: Kids Games',
  [SceneType.ShamailGarden]: 'Shamail Garden',
};
const DEFAULT_SKYBOX_URL =
  'https://d23hwblhugcea5.cloudfront.net/Global/Skybox/';
const DEFAULT_SKYBOX_FILE_NAME = [
  'px.png',
  'nx.png',
  'py.png',
  'ny.png',
  'pz.png',
  'nz.png',
];

const DEFAULT_SCENE_TEMPLATE = `${ASSETS_2022_CDN}/studioAssets/artGallery/`;
const DEFAULT_SCENE_THUMBNAIL = `${ASSETS_2022_CDN}/studioAssets/imageAssets/artGallery.jpg`;
const DEFAULT_SCENE_SKYBOX = {
  skyBoxNumber: 6,
  isSkyBox2025: false,
  hdriType: 'day',
  skyBoxUrl: `${DEFAULT_SKYBOX_URL}7/`,
  fileName: DEFAULT_SKYBOX_FILE_NAME,
  envMapType: EnvMapType.Hdri,
};
const DEFAULT_SCENE_LIGHTS = [
  {
    type: LIGHT_TYPE.DIRECTIONAL,
    position: [0, 10, 0],
    color: 0xffffff,
    intensity: 1,
    isActiveShadow: true,
  },
  {
    type: LIGHT_TYPE.AMBIENT,
    color: 0xffffff,
    position: [0, 0, 0],
    intensity: 0.25,
  },
  {
    type: LIGHT_TYPE.POINTLIGHT,
    position: [0, 20, 0],
    color: 0xffffff,
    intensity: 6,
    decay: 0.5,
    distance: 0,
  },
];

const KIDS_GAMES_SCENE_TEMPLATE = `${ASSETS_2022_CDN}/Dragon/QF/KidsGames/`;
const KIDS_GAMES_SCENE_THUMBNAIL = `${ASSETS_2022_CDN}/Dragon/QF/mediaFolders/QF_Splashimage.jpg`;
const KIDS_GAMES_SCENE_SKYBOX = {
  skyBoxNumber: 45,
  isSkyBox2025: false,
  hdriType: 'day',
  skyBoxUrl: `${DEFAULT_SKYBOX_URL}46/`,
  fileName: DEFAULT_SKYBOX_FILE_NAME,
  envMapType: EnvMapType.Hdri,
};
const KIDS_GAMES_SCENE_LIGHTS = [
  {
    type: LIGHT_TYPE.DIRECTIONAL,
    position: [50, 100, -50],
    color: 0xffe5b4,
    intensity: 1.2,
    isActiveShadow: true,
  },
  {
    type: LIGHT_TYPE.DIRECTIONAL,
    position: [-50, 100, -50],
    color: 0xffe5b4,
    intensity: 1.2,
    isActiveShadow: false,
  },
  {
    type: LIGHT_TYPE.AMBIENT,
    color: 0xfff1d0,
    position: [0, 0, 0],
    intensity: 0.3,
  },
  {
    type: LIGHT_TYPE.POINTLIGHT,
    position: [36, 2, 39],
    color: 0xffffff,
    intensity: 1.2,
    decay: 0.1,
    distance: 6,
  },
  {
    type: LIGHT_TYPE.HEMISPHERELIGHT,
    color: 0xffe5b4,
    position: [0, 0, 0],
    groundColor: 0xd2b48c,
    intensity: 0.4,
  },
];

const QATAR_SCENE_TEMPLATE = `${ASSETS_2022_CDN}/Dragon/QF/MFE/`;
const QATAR_SCENE_THUMBNAIL = `${ASSETS_2022_CDN}/Dragon/QF/mediaFolders/QF_Splashimage.jpg`;
const QATAR_SCENE_SKYBOX = {
  skyBoxNumber: 43,
  isSkyBox2025: false,
  hdriType: 'day',
  skyBoxUrl: `${DEFAULT_SKYBOX_URL}44/`,
  fileName: DEFAULT_SKYBOX_FILE_NAME,
  envMapType: EnvMapType.Hdri,
};
const QATAR_SCENE_LIGHTS = [
  {
    type: LIGHT_TYPE.DIRECTIONAL,
    position: [50, 100, -50],
    color: 0xffe5b4,
    intensity: 1.2,
    isActiveShadow: true,
  },
  {
    type: LIGHT_TYPE.DIRECTIONAL,
    position: [-50, 100, -50],
    color: 0xffe5b4,
    intensity: 1.2,
    isActiveShadow: false,
  },
  {
    type: LIGHT_TYPE.AMBIENT,
    color: 0xfff1d0,
    position: [0, 0, 0],
    intensity: 0.3,
  },
  {
    type: LIGHT_TYPE.POINTLIGHT,
    position: [36, 2, 39],
    color: 0xffffff,
    intensity: 1.2,
    decay: 0.1,
    distance: 6,
  },
  {
    type: LIGHT_TYPE.HEMISPHERELIGHT,
    color: 0xffe5b4,
    position: [0, 0, 0],
    groundColor: 0xd2b48c,
    intensity: 0.4,
  },
];

const SHAMAILGARDEN_SCENE_TEMPLATE = `${ASSETS_2022_CDN}/Dragon/QF/Qatar_Garden/`;
const SHAMAILGARDEN_SCENE_THUMBNAIL = `${ASSETS_2022_CDN}/Dragon/QF/mediaFolders/QF_Splashimage.jpg`;
const SHAMAILGARDEN_SCENE_SKYBOX = {
  skyBoxNumber: 46,
  isSkyBox2025: false,
  hdriType: 'night',
  skyBoxUrl: `${DEFAULT_SKYBOX_URL}47/`,
  fileName: DEFAULT_SKYBOX_FILE_NAME,
  envMapType: EnvMapType.Hdri,
};
const SHAMAILGARDEN_SCENE_LIGHTS = [
  {
    type: LIGHT_TYPE.DIRECTIONAL,
    position: [-30, 20, 10],
    color: 0xffcc88,
    intensity: 0.6,
    isActiveShadow: true,
  },
  {
    type: LIGHT_TYPE.AMBIENT,
    color: 0x334466,
    position: [0, 0, 0],
    intensity: 0.3,
  },
  {
    type: LIGHT_TYPE.POINTLIGHT,
    position: [5, 2, -10],
    color: 0xffaa88,
    intensity: 0.4,
    decay: 0,
    distance: 20,
  },
  {
    type: LIGHT_TYPE.HEMISPHERELIGHT,
    color: 0x223344,
    position: [0, 0, 0],
    groundColor: 0x111111,
    intensity: 0.2,
  },
];

interface ISceneConfig {
  loadSceneTemplate: (baseModelURL: string, sceneThumbnail: string) => void;
  loadLightsTemplate: (lights: TemplateLight[], lightTemplate?: number) => void;
  loadSkyBox: (props: {
    skyBoxNumber: number;
    isSkyBox2025: boolean;
    skyBoxUrl: string;
    hdriType: string;
    fileName: string[];
    envMapType: EnvMapType;
  }) => void;
  saveScene: () => void;
}

export default function SceneConfig({
  loadSceneTemplate,
  loadLightsTemplate,
  saveScene,
  loadSkyBox,
}: ISceneConfig) {
  const router = useRouter();
  const sceneId = Array.isArray(router.query?.id)
    ? router.query?.id[0]
    : router.query?.id;

  const [sceneType, setSceneType] = useState<SceneType>(SceneType.Default);
  const [isShareWithTeam, setIsShareWithTeam] = useState<boolean | undefined>();
  const [isDisplayMinimap, setIsDisplayMinimap] = useState<boolean>(false);
  const [isDisplayStartModal, setIsDisplayStartModal] = useState<boolean>(true);
  const [updateScene] = useUpdateUserSceneMutation();

  const { data: teamData } = useGetMyTeamQuery();
  const team = teamData?.getMyTeam.team;

  const { data } = useUserScenesQuery({
    variables: {
      where: {
        id: {
          equals: sceneId as any,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const scene = data?.userScenes[0];

  const onSaveDebounced = useMemo(
    () =>
      debounce(
        async (
          sceneType: SceneType,
          isShareWithTeam: boolean,
          teamId: string | null,
          isDisplayMinimap: boolean,
          isDisplayStartModal: boolean
        ) => {
          const update = await updateScene({
            variables: {
              data: {
                sceneType: {
                  set: sceneType,
                },
                teamId: {
                  set: isShareWithTeam ? teamId : null,
                },
                displayMinimap: {
                  set: isDisplayMinimap,
                },
                displayStartModal: {
                  set: isDisplayStartModal,
                },
              },
              where: {
                id: sceneId,
              },
            },
            refetchQueries: [UserScenesDocument],
          });

          if (update.errors) {
            openNotification(
              'Error',
              'Error during saving changes.',
              'topRight',
              2,
              'error'
            );
          } else {
            openNotification(
              'Success',
              'Successfully updated scene configuration.',
              'topRight',
              2,
              'success'
            );
          }
        },
        800
      ),
    []
  );

  function onChangeSceneType(value: SceneType) {
    onSaveDebounced.cancel();
    setSceneType(value);

    const sceneConfigs = {
      [SceneType.Qatar]: {
        template: QATAR_SCENE_TEMPLATE,
        thumbnail: QATAR_SCENE_THUMBNAIL,
        skybox: QATAR_SCENE_SKYBOX,
        lights: QATAR_SCENE_LIGHTS,
      },
      [SceneType.KidsGame]: {
        template: KIDS_GAMES_SCENE_TEMPLATE,
        thumbnail: KIDS_GAMES_SCENE_THUMBNAIL,
        skybox: KIDS_GAMES_SCENE_SKYBOX,
        lights: KIDS_GAMES_SCENE_LIGHTS,
      },
      [SceneType.ShamailGarden]: {
        template: SHAMAILGARDEN_SCENE_TEMPLATE,
        thumbnail: SHAMAILGARDEN_SCENE_THUMBNAIL,
        skybox: SHAMAILGARDEN_SCENE_SKYBOX,
        lights: SHAMAILGARDEN_SCENE_LIGHTS,
      },
      default: {
        template: DEFAULT_SCENE_TEMPLATE,
        thumbnail: DEFAULT_SCENE_THUMBNAIL,
        skybox: DEFAULT_SCENE_SKYBOX,
        lights: DEFAULT_SCENE_LIGHTS,
      },
    };

    const config = sceneConfigs[value] ?? sceneConfigs.default;

    loadSceneTemplate(config.template, config.thumbnail);
    loadSkyBox(config.skybox);
    loadLightsTemplate(config.lights, 0);

    onSaveDebounced(
      value,
      isShareWithTeam || false,
      team?.id ?? null,
      isDisplayMinimap!,
      isDisplayStartModal!
    );
  }

  useEffect(() => {
    if (scene?.sceneType) {
      setSceneType(scene?.sceneType as any);
    }
  }, [scene]);

  useEffect(() => {
    if (typeof isShareWithTeam === 'boolean' || !scene?.teamId) {
      return;
    }

    setIsShareWithTeam(!!scene?.teamId);
  }, [scene?.teamId]);

  useEffect(() => {
    if (!scene) {
      return;
    }

    setIsDisplayMinimap(!!scene.displayMinimap);
  }, [scene]);

  useEffect(() => {
    if (!scene) {
      return;
    }

    setIsDisplayStartModal(!!scene.displayStartModal);
  }, [scene]);

  useEffect(() => {
    return () => onSaveDebounced.cancel();
  }, [onSaveDebounced]);

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700 }}>Scene Config:</h1>
      <p style={{ marginBottom: 5, marginTop: 20 }}>Select scene type</p>
      <Select
        style={{ width: '100%' }}
        onChange={onChangeSceneType}
        defaultValue={sceneType}
        value={sceneType}
      >
        {Object.keys(sceneMapping).map((value, key) => {
          return (
            <Select.Option key={`${value}-${key}`} value={value}>
              {sceneMapping[value]}
            </Select.Option>
          );
        })}
      </Select>
      {team && scene && (
        <>
          <Title level={4} style={{ marginBottom: 5, marginTop: 20 }}>
            Team Project settings
          </Title>
          <Checkbox
            defaultChecked={!!scene.teamId}
            value={isShareWithTeam}
            onChange={(e) => {
              onSaveDebounced.cancel();
              setIsShareWithTeam(e.target.checked);
              onSaveDebounced(
                sceneType,
                e.target.checked,
                team?.id ?? null,
                isDisplayMinimap!,
                isDisplayStartModal!
              );
            }}
          />{' '}
          <Text>Share this scene with Team</Text>
        </>
      )}
      {scene && (
        <>
          <Title level={4} style={{ marginBottom: 5, marginTop: 20 }}>
            Enable Minimap
          </Title>
          <Checkbox
            defaultChecked={scene.displayMinimap!}
            value={isDisplayMinimap}
            onChange={(e) => {
              onSaveDebounced.cancel();
              setIsDisplayMinimap(e.target.checked);
              onSaveDebounced(
                sceneType,
                isShareWithTeam || false,
                team?.id ?? null,
                e.target.checked,
                isDisplayStartModal!
              );
            }}
          />{' '}
          <Text>Show minimap in this scene</Text>
        </>
      )}
      {scene && (
        <>
          <Title level={4} style={{ marginBottom: 5, marginTop: 20 }}>
            Welcome Modal Settings
          </Title>
          <Checkbox
            defaultChecked={scene.displayStartModal ?? true}
            checked={isDisplayStartModal}
            onChange={(e) => {
              onSaveDebounced.cancel();
              setIsDisplayStartModal(e.target.checked);
              onSaveDebounced(
                sceneType,
                isShareWithTeam || false,
                team?.id ?? null,
                isDisplayMinimap!,
                e.target.checked
              );
            }}
          />{' '}
          <Text>Display welcome modal when scene starts</Text>
        </>
      )}
    </div>
  );
}
